import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'accent' | 'outline';

const TONES: Record<Tone, string> = {
  neutral: 'bg-muted text-muted-foreground border-transparent',
  info: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:border-blue-500/20',
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/20',
  warning: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/20',
  danger: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/20',
  accent: 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-500/10 dark:text-teal-300 dark:border-teal-500/20',
  outline: 'bg-transparent text-foreground border-border'
};

export function Badge({ tone = 'neutral', className, children }: { tone?: Tone; className?: string; children: ReactNode }) {
  return (
    <span className={cn('inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[11px] font-medium leading-4', TONES[tone], className)}>
      {children}
    </span>
  );
}

const STATUS_MAP: Record<string, { label: string; tone: Tone }> = {
  draft: { label: 'Borrador', tone: 'neutral' },
  confirmed: { label: 'Confirmado', tone: 'info' },
  in_production: { label: 'En producción', tone: 'accent' },
  partially_shipped: { label: 'Parcial', tone: 'warning' },
  shipped: { label: 'Expedido', tone: 'success' },
  cancelled: { label: 'Cancelado', tone: 'danger' },
  pending: { label: 'Pendiente', tone: 'warning' },
  in_progress: { label: 'En curso', tone: 'info' },
  blocked: { label: 'Bloqueado', tone: 'danger' },
  completed: { label: 'Completado', tone: 'success' },
  active: { label: 'Activa', tone: 'success' },
  retired: { label: 'Retirada', tone: 'neutral' },
  created: { label: 'Creada', tone: 'neutral' },
  kit_pending: { label: 'Kit pendiente', tone: 'warning' },
  kit_ready: { label: 'Kit listo', tone: 'info' },
  packed: { label: 'Embalada', tone: 'accent' },
  ready_to_ship: { label: 'Lista para logística', tone: 'success' },
  validated: { label: 'Validada', tone: 'success' },
  pending_review: { label: 'Por revisar', tone: 'warning' },
  rejected: { label: 'Rechazada', tone: 'danger' },
  partially_received: { label: 'Parcial', tone: 'warning' },
  received: { label: 'Recibido', tone: 'success' },
  done: { label: 'Completado', tone: 'success' },
  queued: { label: 'En cola', tone: 'neutral' },
  sent: { label: 'Enviado', tone: 'info' },
  error: { label: 'Error', tone: 'danger' },
  label_generated: { label: 'Etiqueta generada', tone: 'info' },
  in_transit: { label: 'En tránsito', tone: 'accent' },
  delivered: { label: 'Entregado', tone: 'success' },
  reserved: { label: 'Reservado', tone: 'info' },
  released: { label: 'Liberado', tone: 'accent' },
  released_partial: { label: 'Parcial', tone: 'warning' },
  open: { label: 'Abierta', tone: 'warning' },
  acknowledged: { label: 'Revisada', tone: 'info' },
  resolved: { label: 'Resuelta', tone: 'success' },
  inactive: { label: 'Inactivo', tone: 'neutral' },
  locked: { label: 'Bloqueado', tone: 'danger' },
  partial: { label: 'Parcial', tone: 'warning' },
  complete: { label: 'Completo', tone: 'success' },
  critical: { label: 'Crítico', tone: 'danger' },
  warning: { label: 'Aviso', tone: 'warning' },
  info: { label: 'Info', tone: 'info' }
};

export function StatusBadge({ status, className }: { status: string | null | undefined; className?: string }) {
  const item = STATUS_MAP[status ?? ''] ?? { label: status ?? '—', tone: 'neutral' as Tone };
  return <Badge tone={item.tone} className={className}>{item.label}</Badge>;
}

export function humanize(value: string | null | undefined): string {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}
